import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AirRestrictionStatus } from "@hopper-b2b/types";
import { Typography } from "@material-ui/core";
import { IconName } from "../../Icon";
import { IconComponent } from "../../IconComponent";
import { IconContentView } from "../../IconContentView";

export interface IRestrictionProps {
  symbol: AirRestrictionStatus;
  description: string;
  category?: string;
  group?: string;
}

/**
 * Maps baggage category identifiers to their corresponding luggage icon types.
 * Used to display category-specific icons for included baggage amenities.
 *
 * @constant
 * @type {Record<string, IconName>}
 */
export const BAGGAGE_CATEGORY_ICONS: Record<string, IconName> = {
  "personal-item": IconName.PersonalItemLuggage,
  "carry-on-allowance": IconName.CabinLuggage,
  "checked-bag-allowance": IconName.CheckedLuggage,
};

const restrictionIcon: { [k in AirRestrictionStatus]: JSX.Element } = {
  [AirRestrictionStatus.INCLUDED]: (
    <FontAwesomeIcon
      className="icon-available"
      icon={faCheckCircle as IconProp}
    />
  ),
  [AirRestrictionStatus.PURCHASABLE]: (
    <IconComponent
      className="icon-paid"
      name={IconName.MoneyGBP}
      aria-hidden={true}
    />
  ),
  [AirRestrictionStatus.UNAVAILABLE]: (
    <IconComponent
      className="icon-unavailable"
      name={IconName.Forbidden}
      aria-hidden={true}
    />
  ),
  [AirRestrictionStatus.GENERIC]: (
    <IconComponent
      className="icon-generic"
      name={IconName.Generic}
      aria-hidden={true}
    />
  ),
  [AirRestrictionStatus.UNKNOWN]: null,
};
const Restriction = (props: IRestrictionProps) => {
  const { symbol, description, category } = props;

  // For included baggage items, use luggage-specific icons
  const shouldUseLuggageIcon =
    symbol === AirRestrictionStatus.INCLUDED &&
    category &&
    category in BAGGAGE_CATEGORY_ICONS;

  const icon = shouldUseLuggageIcon ? (
    <IconComponent
      className="icon-luggage"
      name={BAGGAGE_CATEGORY_ICONS[category]}
      aria-hidden={true}
    />
  ) : (
    restrictionIcon[symbol]
  );

  return (
    <IconContentView
      className="fare-details-restriction"
      icon={icon}
      content={<Typography variant="body2">{description}</Typography>}
    />
  );
};

export default Restriction;
